import api from '../utils/api';
import type { Medication } from './medicationService';

export type RefillRequestChannel = 'PHONE' | 'WALK_IN' | 'EMAIL' | 'SMS';
export type RefillRequestStatus =
  | 'NEEDS_VERIFICATION'
  | 'PENDING_VET'
  | 'APPROVED'
  | 'DENIED'
  | 'RECHECK_REQUESTED';

export interface RxRefillRequest {
  id: string;
  clinicId: string;
  medicationId?: string | null;
  medication?: Pick<
    Medication,
    'id' | 'name' | 'dosage' | 'instructions' | 'refillsAuthorized' | 'refillsRemaining' | 'deaSchedule' | 'expirationDate' | 'horseId'
  > & { _count?: { dispenseEvents: number } };
  horseId?: string | null;
  horseName?: string | null;
  ownerName?: string | null;
  ownerContact?: string | null;
  channel: RefillRequestChannel;
  rawIntakeBody?: string | null;
  intakeMessageId?: string | null;
  status: RefillRequestStatus;
  decidedAt?: string | null;
  decidedById?: string | null;
  decidedBy?: { id: string; name: string; email: string } | null;
  decisionNotes?: string | null;
  dispenseEventId?: string | null;
  assignedVetId?: string | null;
  assignedVet?: { id: string; name: string; email: string } | null;
  createdAt: string;
  updatedAt: string;
}

export const refillRequestService = {
  async list(filters?: { status?: RefillRequestStatus; channel?: RefillRequestChannel }) {
    const response = await api.get<RxRefillRequest[]>('/refill-requests', { params: filters });
    return response.data;
  },

  async getOne(id: string) {
    const response = await api.get<RxRefillRequest>(`/refill-requests/${id}`);
    return response.data;
  },

  async create(data: {
    channel: RefillRequestChannel;
    medicationId?: string;
    horseId?: string;
    horseName?: string;
    ownerName?: string;
    ownerContact?: string;
    rawIntakeBody?: string;
    intakeMessageId?: string;
    assignedVetId?: string;
  }) {
    const response = await api.post<RxRefillRequest>('/refill-requests', data);
    return response.data;
  },

  async verify(id: string) {
    const response = await api.post<RxRefillRequest>(`/refill-requests/${id}/verify`);
    return response.data;
  },

  async approve(
    id: string,
    data: { decisionNotes?: string; dispenseQuantity?: number; dispenseUnit?: string },
  ) {
    const response = await api.post<RxRefillRequest>(`/refill-requests/${id}/approve`, data);
    return response.data;
  },

  async deny(id: string, data: { decisionNotes?: string }) {
    const response = await api.post<RxRefillRequest>(`/refill-requests/${id}/deny`, data);
    return response.data;
  },

  async requestRecheck(id: string, data: { decisionNotes?: string }) {
    const response = await api.post<RxRefillRequest>(`/refill-requests/${id}/request-recheck`, data);
    return response.data;
  },
};
